import React from 'react'
import {useHistory, useLocation} from 'react-router'
import {navigateIfUserRegistered} from 'Src/actions/RegistrationActions'
import ProgressButton from 'Src/components/BreadCrumbs/ProgressButton'
import ProgressSegment from 'Src/components/BreadCrumbs/ProgressSegment'
import {useCustomerStateContext} from 'Src/contexts/CustomerContext'
import {useDealStateContext} from 'Src/contexts/DealContext'
import {useFrameContext} from 'Src/contexts/FrameContext'
import {useNavDispatchContext} from 'Src/contexts/NavContext'
import {useProductContext} from 'Src/contexts/ProductContext'
import {
  PROGRESS_STEP,
  useProgressDispatchContext,
  useProgressStateContext,
} from 'Src/contexts/ProgressContext'
import {ACTION_DETAILS} from 'Src/services/analytics/constants'
import {useAnalytics} from 'Src/services/analytics/useAnalytics'
import {breadcrumbProgressTracking} from 'Src/utilities/Helpers'
import {trackProductConversionEvent} from 'Src/utilities/ProductHelpers'

const Breadcrumbs = () => {
  const location = useLocation()
  const history = useHistory()
  const customerState = useCustomerStateContext()
  const {customerData: customer} = customerState
  const navDispatch = useNavDispatchContext()
  const progress = useProgressStateContext()
  const ProgressDispatch = useProgressDispatchContext()
  const {isUpdating, dealType} = useDealStateContext()
  const {trackEvent, events} = useAnalytics()
  const {getSelectedRatedProducts, hasProducts} = useProductContext()
  const {isDocUploadsEnabled, isTradeLocked} = useFrameContext()

  const trackProductEvents = () => {
    if (hasProducts && location?.pathname === '/products') {
      trackProductConversionEvent(
        customer?.id,
        events,
        getSelectedRatedProducts(dealType),
        trackEvent
      )
    }
  }

  const handlePaymentProgressClick = (event) => {
    event.preventDefault()

    if (isUpdating) {
      return
    }

    trackEvent(events.NAVIGATION, events.NAVIGATION.actions.NAVIGATE, ACTION_DETAILS.PAGES.PAYMENT)
    trackProductEvents()
    history.push('/')
    ProgressDispatch({
      type: 'update',
      payload: breadcrumbProgressTracking(progress, PROGRESS_STEP.payment, dealType),
    })
  }

  const handleTradeProgressClick = (event) => {
    event.preventDefault()

    if (isUpdating) {
      return
    }

    trackEvent(events.NAVIGATION, events.NAVIGATION.actions.NAVIGATE, ACTION_DETAILS.PAGES.TRADE)
    trackProductEvents()
    navigateIfUserRegistered({
      customerId: customer.id,
      navigationPath: '/trade',
      navDispatch,
      history,
      optionalFn: () => {
        ProgressDispatch({
          type: 'update',
          payload: breadcrumbProgressTracking(progress, PROGRESS_STEP.trade, dealType),
        })
      },
      skipAuthCheck: !isTradeLocked,
    })
  }

  const handleProductsProgressClick = (event) => {
    event.preventDefault()

    if (isUpdating) {
      return
    }

    trackEvent(events.NAVIGATION, events.NAVIGATION.actions.NAVIGATE, ACTION_DETAILS.PAGES.PRODUCTS)
    navigateIfUserRegistered({
      customerId: customer.id,
      navigationPath: '/products',
      navDispatch,
      history,
      optionalFn: () => {
        ProgressDispatch({
          type: 'update',
          payload: breadcrumbProgressTracking(progress, PROGRESS_STEP.products, dealType),
        })
      },
    })
  }

  const handleCreditProgressClick = (event) => {
    event.preventDefault()

    if (isUpdating) {
      return
    }

    trackEvent(events.NAVIGATION, events.NAVIGATION.actions.NAVIGATE, ACTION_DETAILS.PAGES.CREDIT)
    trackProductEvents()
    navigateIfUserRegistered({
      customerId: customer.id,
      navigationPath: '/credit',
      navDispatch,
      history,
      optionalFn: () => {
        ProgressDispatch({
          type: 'update',
          payload: breadcrumbProgressTracking(progress, PROGRESS_STEP.credit, dealType),
        })
      },
    })
  }

  const handleDocumentUploadsProgressClick = (event) => {
    event.preventDefault()

    if (isUpdating) {
      return
    }

    trackEvent(
      events.NAVIGATION,
      events.NAVIGATION.actions.NAVIGATE,
      ACTION_DETAILS.PAGES.DOC_UPLOADS
    )
    navigateIfUserRegistered({
      customerId: customer.id,
      navigationPath: '/document-upload',
      navDispatch,
      history,
      optionalFn: () => {
        ProgressDispatch({
          type: 'update',
          payload: breadcrumbProgressTracking(progress, PROGRESS_STEP.uploads, dealType),
        })
      },
    })
  }

  const handleFinalizeProgressClick = (event) => {
    event.preventDefault()

    if (isUpdating) {
      return
    }

    trackEvent(events.NAVIGATION, events.NAVIGATION.actions.NAVIGATE, ACTION_DETAILS.PAGES.FINALIZE)
    trackProductEvents()
    navigateIfUserRegistered({
      customerId: customer.id,
      navigationPath: '/finalize',
      navDispatch,
      history,
      optionalFn: () => {
        ProgressDispatch({
          type: 'update',
          payload: breadcrumbProgressTracking(progress, PROGRESS_STEP.finalize, dealType),
        })
      },
    })
  }

  if (!progress) {
    return null
  }

  return (
    <div className="tw-relative tw-m-3 sm:tw-left-7 sm:tw-h-72 sm:tw-w-4/6">
      <div className="line-position tw-absolute tw-left-10 tw-flex tw-justify-around sm:tw-left-0 sm:tw-top-0 sm:tw-h-full sm:tw-w-full sm:tw-flex-col sm:tw-items-center">
        <ProgressSegment type="payment" />
        <ProgressSegment type="trade" />
        {hasProducts ? <ProgressSegment type="products" /> : null}
        <ProgressSegment type="credit" />
        {isDocUploadsEnabled ? <ProgressSegment type="uploads" /> : null}
      </div>
      <div className="tw-flex tw-w-full tw-justify-between tw-text-xs tw-font-semibold tw-text-brand-body_text--primary sm:tw-h-full sm:tw-flex-col sm:tw-content-between sm:tw-items-center sm:tw-text-base">
        <ProgressButton onClick={handlePaymentProgressClick} type="payment" />
        <ProgressButton onClick={handleTradeProgressClick} type="trade" />
        {hasProducts ? (
          <ProgressButton onClick={handleProductsProgressClick} type="products" name="protection" />
        ) : null}
        <ProgressButton onClick={handleCreditProgressClick} type="credit" />
        {isDocUploadsEnabled ? (
          <ProgressButton
            onClick={handleDocumentUploadsProgressClick}
            name="documents"
            type="uploads"
          />
        ) : null}
        <ProgressButton onClick={handleFinalizeProgressClick} type="finalize" />
      </div>
    </div>
  )
}

export default Breadcrumbs
